import { Copy, Link2, Loader2, Share2 } from "lucide-react";
import { type JSX, useEffect, useId, useRef, useState } from "react";

import { useToast } from "@/components/ui/toast-provider";
import { describeAssetLocator } from "@/lib/asset-locator-labels";
import { copyTextToClipboard } from "@/lib/clipboard";
import type {
  SafeReportProjection,
  ShareAudience,
  ShareGrantState,
  SharePreviewResponse,
} from "@/lib/reports";
import {
  getShareState,
  previewShare,
  publicShareUrl,
  revokeShare,
  setShareAudience,
  toReportImageRelation,
} from "@/lib/reports";
import { useFocusTrap } from "@/lib/use-focus-trap";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  reportId: string;
  revisionNumber: number;
}

const AUDIENCE_LABEL: Record<ShareAudience, string> = {
  private: "Solo yo",
  beflow_team: "Equipo BeFlow",
  anyone_with_link: "Cualquiera con el enlace",
};

export function ShareDialog({
  open,
  onClose,
  sessionId,
  reportId,
  revisionNumber,
}: ShareDialogProps): JSX.Element | null {
  const { showToast } = useToast();
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useFocusTrap(dialogRef, open);

  const [state, setState] = useState<ShareGrantState | null>(null);
  const [preview, setPreview] = useState<SharePreviewResponse | null>(null);
  const [audience, setAudience] = useState<ShareAudience>("private");
  const [allowPdf, setAllowPdf] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const current = await getShareState({ sessionId, reportId, revisionNumber });
        if (cancelled) return;
        setState(current);
        setAudience(current.audience);
        setAllowPdf(current.allow_pdf);
        setSecret(null);
        if (current.audience !== "private") {
          const nextPreview = await previewShare({
            sessionId,
            reportId,
            revisionNumber,
            audience: current.audience,
          });
          if (!cancelled) setPreview(nextPreview);
        } else {
          setPreview(null);
        }
      } catch {
        if (!cancelled) setError("No se pudo cargar el estado de compartición.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, reportId, revisionNumber]);

  if (!open) return null;

  async function applyAudience(next: ShareAudience, replaceToken = false): Promise<void> {
    setLoading(true);
    setError(null);
    setStatusMessage("");
    try {
      if (next !== "private") {
        const nextPreview = await previewShare({
          sessionId,
          reportId,
          revisionNumber,
          audience: next,
        });
        setPreview(nextPreview);
      }
      const result = await setShareAudience({
        sessionId,
        reportId,
        revisionNumber,
        body: {
          audience: next,
          allow_pdf: allowPdf,
          replace_token: replaceToken,
          expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        },
      });
      setState(result.state);
      setAudience(result.state.audience);
      setPreview(result.preview);
      if (result.capability_secret) {
        setSecret(result.capability_secret);
        setStatusMessage("Enlace creado. Cópialo ahora; no se podrá recuperar después.");
      } else if (next === "private") {
        setSecret(null);
        setStatusMessage("Acceso revocado. Los PDF ya descargados no se pueden recuperar.");
      } else {
        showToast({ kind: "success", message: "Configuración actualizada." });
      }
    } catch (err) {
      const message =
        err instanceof Error && err.message.includes("public_share_blocked")
          ? "Hay metadatos internos en el texto libre. Corrige el informe en una nueva revisión."
          : "No se pudo actualizar el acceso.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy(): Promise<void> {
    if (!secret) return;
    const copied = await copyTextToClipboard(publicShareUrl(secret));
    showToast({
      kind: copied ? "success" : "error",
      message: copied
        ? "Enlace copiado al portapapeles."
        : "No se pudo copiar el enlace al portapapeles.",
    });
  }

  async function handleRevoke(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const next = await revokeShare({ sessionId, reportId, revisionNumber });
      setState(next);
      setAudience("private");
      setSecret(null);
      setPreview(null);
      setStatusMessage(
        "Enlace revocado. Esto no puede recuperar un PDF que ya haya sido descargado.",
      );
    } catch {
      setError("No se pudo revocar el acceso.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <dialog
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-background p-5 shadow-xl open:flex open:flex-col"
        open
        ref={dialogRef}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-body-1 font-semibold" id={titleId}>
              <Share2 size={18} /> Compartir informe
            </h2>
            <p className="mt-1 text-body-3 text-muted-foreground">
              Revisión v{revisionNumber}. Cambiar audiencia no crea una nueva revisión.
            </p>
          </div>
          <button
            aria-label="Cerrar"
            className="rounded-md border border-border px-2 py-1 text-body-3"
            onClick={onClose}
            type="button"
          >
            Cerrar
          </button>
        </div>

        {loading && !state ? (
          <p className="flex items-center gap-2 py-8 text-body-3 text-muted-foreground">
            <Loader2 className="animate-spin" size={16} /> Cargando…
          </p>
        ) : (
          <div className="space-y-4">
            <fieldset className="space-y-2">
              <legend className="text-body-3 font-medium">Audiencia</legend>
              {(Object.keys(AUDIENCE_LABEL) as ShareAudience[]).map((value) => (
                <label
                  className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3"
                  key={value}
                >
                  <input
                    checked={audience === value}
                    className="mt-1"
                    disabled={loading}
                    name="audience"
                    onChange={() => void applyAudience(value)}
                    type="radio"
                    value={value}
                  />
                  <span>
                    <span className="block text-body-3 font-medium">{AUDIENCE_LABEL[value]}</span>
                    <span className="block text-xs text-muted-foreground">
                      {value === "private" && "Solo el propietario puede ver este informe."}
                      {value === "beflow_team" &&
                        "Cuentas activas de este despliegue BeFlow, solo lectura."}
                      {value === "anyone_with_link" &&
                        "Enlace anónimo revisable, revocable y fijado a esta revisión."}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            {preview && audience !== "private" ? (
              <section
                aria-label="Vista previa segura"
                className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
              >
                <p className="text-body-3">
                  Se comparten la narrativa y las conclusiones. Fuentes con detalle oculto:{" "}
                  <strong>{preview.redacted_source_count}</strong>.
                </p>
                {preview.team_visibility_note ? (
                  <p className="text-xs text-muted-foreground">{preview.team_visibility_note}</p>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  Revocar impide el acceso futuro, pero no puede recuperar un PDF ya descargado.
                </p>
                <ProjectionMiniPreview projection={preview.projection} />
              </section>
            ) : null}

            {audience === "anyone_with_link" ? (
              <div className="space-y-2">
                {secret ? (
                  <button
                    aria-label="Copiar enlace"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-extra-2-40 px-3 py-2 text-body-3 font-semibold text-white"
                    onClick={() => void handleCopy()}
                    type="button"
                  >
                    <Copy size={16} />
                    Copiar enlace
                  </button>
                ) : (
                  <button
                    aria-label="Reemplazar enlace"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-body-3"
                    disabled={loading || !state?.has_active_token}
                    onClick={() => void applyAudience("anyone_with_link", true)}
                    type="button"
                  >
                    <Link2 size={16} />
                    {state?.has_active_token
                      ? "Reemplazar enlace (revoca el anterior)"
                      : "Crear enlace público"}
                  </button>
                )}
                {state?.token_prefix ? (
                  <p className="text-xs text-muted-foreground">
                    Prefijo del enlace activo: {state.token_prefix}… (el secreto completo solo se
                    muestra al crear o reemplazar)
                  </p>
                ) : null}
              </div>
            ) : null}

            <details
              className="rounded-md border border-border p-3"
              onToggle={(event) => setAdvancedOpen((event.target as HTMLDetailsElement).open)}
              open={advancedOpen}
            >
              <summary className="cursor-pointer text-body-3 font-medium">
                Configuración del enlace
              </summary>
              <div className="mt-3 space-y-3">
                <label className="flex items-center gap-2 text-body-3">
                  <input
                    checked={allowPdf}
                    disabled={loading || audience === "private"}
                    onChange={(event) => setAllowPdf(event.target.checked)}
                    type="checkbox"
                  />
                  Permitir descarga PDF al destinatario (desactivado por defecto)
                </label>
                <label className="block text-body-3">
                  Expiración opcional
                  <input
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                    disabled={loading || audience === "private"}
                    onChange={(event) => setExpiresAt(event.target.value)}
                    type="datetime-local"
                    value={expiresAt}
                  />
                </label>
                <button
                  className="rounded-md border border-border px-3 py-1.5 text-body-3"
                  disabled={loading || audience === "private"}
                  onClick={() => void applyAudience(audience)}
                  type="button"
                >
                  Guardar configuración
                </button>
                <button
                  className="ml-2 rounded-md border border-destructive/40 px-3 py-1.5 text-body-3 text-destructive"
                  disabled={loading || audience === "private"}
                  onClick={() => void handleRevoke()}
                  type="button"
                >
                  Revocar acceso
                </button>
              </div>
            </details>

            <div aria-live="polite" className="min-h-5 text-body-3 text-muted-foreground">
              {statusMessage}
            </div>
            {error ? (
              <p aria-live="assertive" className="text-body-3 text-destructive" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        )}
      </dialog>
    </div>
  );
}

function ProjectionMiniPreview({
  projection,
}: {
  projection: SafeReportProjection;
}): JSX.Element {
  // The backend's `SafeVisualRelationProjection` wire shape never carries
  // `alt_text`/`caption`/`locator` on a redacted relation (see
  // `toReportImageRelation` in `@/lib/reports`) — normalizing it into the
  // strict two-state `ReportImageRelation` union here means a redacted row
  // below is STRUCTURALLY incapable of rendering those fields, regardless
  // of what a buggy/malicious backend response might additionally include.
  const imageRelations = projection.visual_assets.map(toReportImageRelation);
  const includedImageCount = imageRelations.filter(
    (relation) => relation.state === "available",
  ).length;
  const redactedImageCount = imageRelations.length - includedImageCount;

  return (
    <div className="rounded-md border border-border bg-background p-3 text-xs">
      <p className="font-semibold">{projection.title}</p>
      <p className="mt-1 text-muted-foreground">
        v{projection.revision_number} · {projection.redacted_source_count} fuentes redactadas
        {imageRelations.length > 0
          ? ` · ${includedImageCount} imagen${includedImageCount === 1 ? "" : "es"} incluida${includedImageCount === 1 ? "" : "s"} · ${redactedImageCount} redactada${redactedImageCount === 1 ? "" : "s"}`
          : ""}
      </p>
      <ul className="mt-2 space-y-1">
        {projection.sources.map((source) => (
          <li key={source.handle}>
            {source.redacted ? (
              <em>{source.label}</em>
            ) : (
              <span>
                {source.label}
                {source.url ? ` · ${source.url}` : ""}
              </span>
            )}
          </li>
        ))}
      </ul>
      {imageRelations.length > 0 ? (
        <ul aria-label="Imágenes del informe" className="mt-2 space-y-1">
          {imageRelations.map((relation) =>
            relation.state === "available" ? (
              <li key={relation.relation_id}>
                {relation.alt_text}
                {relation.locator ? ` · ${describeAssetLocator(relation.locator)}` : ""}
              </li>
            ) : (
              <li key={relation.relation_id}>
                <em>Evidencia visual no compartida</em>
              </li>
            ),
          )}
        </ul>
      ) : null}
    </div>
  );
}
